/**
 * Internal helpers shared across the crypto modules.
 *
 * These are NOT part of the public API surface. Everything here is either a
 * thin adapter over reviewed primitives (`sodium-native`, Node `crypto`) or a
 * pure byte utility. No cryptographic constructions are invented here.
 */
import { FumeCryptoError } from './errors.js';
/**
 * Coerce a `Uint8Array` view into a Node `Buffer` that aliases the same memory
 * (no copy) so it can be handed to `sodium-native`. `Buffer` is a subclass of
 * `Uint8Array`, so callers can pass either.
 */
export function asBuffer(view) {
    if (Buffer.isBuffer(view))
        return view;
    return Buffer.from(view.buffer, view.byteOffset, view.byteLength);
}
/** Allocate a fresh zeroed Node `Buffer` of the given length. */
export function alloc(length) {
    return Buffer.alloc(length);
}
/** Copy bytes into a brand-new, standalone `Uint8Array` (defensive return). */
export function toUint8Array(view) {
    return Uint8Array.from(view);
}
/** Constant-ish length assertion that throws `FumeCryptoError` on mismatch. */
export function requireLength(value, expected, name) {
    if (!(value instanceof Uint8Array)) {
        throw new FumeCryptoError(`${name} must be a Uint8Array`);
    }
    if (value.length !== expected) {
        throw new FumeCryptoError(`${name} must be exactly ${expected} bytes, got ${value.length}`);
    }
}
/** True iff every byte of `value` is zero. */
export function isAllZero(value) {
    let acc = 0;
    for (let i = 0; i < value.length; i++)
        acc |= value[i];
    return acc === 0;
}
/**
 * 8-byte big-endian unsigned encoding of a non-negative integer. Used for the
 * unambiguous `uint64be(length) || bytes` length framing shared by the S1 hash
 * constructions (selector, share commitment).
 */
export function uint64be(x) {
    if (!Number.isInteger(x) || x < 0) {
        throw new FumeCryptoError(`uint64be requires a non-negative integer, got ${x}`);
    }
    const out = new Uint8Array(8);
    const view = new DataView(out.buffer);
    view.setBigUint64(0, BigInt(x), false);
    return out;
}
/** Concatenate several byte arrays into one fresh `Uint8Array`. */
export function concatBytes(...parts) {
    let total = 0;
    for (const p of parts)
        total += p.length;
    const out = new Uint8Array(total);
    let offset = 0;
    for (const p of parts) {
        out.set(p, offset);
        offset += p.length;
    }
    return out;
}
/** Constant-time equality for two byte arrays of equal (or differing) length. */
export function bytesEqual(a, b) {
    if (a.length !== b.length)
        return false;
    let acc = 0;
    for (let i = 0; i < a.length; i++)
        acc |= a[i] ^ b[i];
    return acc === 0;
}
//# sourceMappingURL=internal.js.map